import type {
  AnswerKey, DemandProfile, Difficulty, GenContext, Question, QuestionKind, ResponseSpec, RubricCriterion,
  Stimulus, SubjectId,
} from '@/core/types';
import type { Rng } from '@/core/util/rng';
import { normalizeAnswer, uid } from '@/core/util/misc';

/**
 * Question-construction helpers shared by every generator.
 * Generators describe structure; these helpers produce a consistent Question.
 */

const STEPS: number[] = [1, 1, 2, 3, 3, 4, 5];
const ABS: DemandProfile['abstraction'][] = [0, 0, 1, 1, 2, 2, 3];
const TRANSFER: DemandProfile['transfer'][] = [0, 0, 0, 1, 2, 2, 3];
const LOAD: DemandProfile['infoLoad'][] = [0, 1, 1, 1, 2, 2, 3];
const DISTRACT: DemandProfile['distractorQuality'][] = [1, 1, 2, 2, 2, 3, 3];

export function demandFor(d: Difficulty, mode: GenContext['mode'] = 'practice', o: Partial<DemandProfile> = {}): DemandProfile {
  const scaffold: DemandProfile['scaffolding'] =
    mode === 'guided' || mode === 'worked' ? 3 : mode === 'learn' ? 2 : mode === 'boss' || mode === 'exam' ? 0 : d <= 2 ? 1 : 0;
  return {
    steps: STEPS[d - 1],
    abstraction: ABS[d - 1],
    transfer: TRANSFER[d - 1],
    scaffolding: scaffold,
    infoLoad: LOAD[d - 1],
    distractorQuality: DISTRACT[d - 1],
    combinesConcepts: d >= 5,
    ...o,
  };
}

export interface QDraft {
  form: string;
  params: string;
  kind: QuestionKind;
  difficulty: Difficulty;
  prompt: string;
  context?: string;
  stimulus?: Stimulus;
  response: ResponseSpec;
  answer: AnswerKey;
  hints?: string[];
  solution: string[];
  misconceptionMap?: Record<string, string>;
  demand?: Partial<DemandProfile>;
  involvesSkills?: string[];
  objective?: string;
  time?: number;
  provenance?: Question['provenance'];
  speechLang?: string;
}

/** Bind helpers to a skill so generators stay terse. */
export function forSkill(skillId: string, subjectId: SubjectId, familyId = `${skillId}#core`) {
  return (ctx: GenContext, d: QDraft): Question => ({
    id: uid('q'),
    fingerprint: `${skillId}|${d.form}|${d.params}`,
    skillId,
    subjectId,
    familyId,
    objective: d.objective ?? d.form,
    kind: d.kind,
    difficulty: d.difficulty,
    demand: demandFor(d.difficulty, ctx.mode, d.demand),
    prompt: d.prompt,
    context: d.context,
    stimulus: d.stimulus,
    response: d.response,
    answer: d.answer,
    hints: d.hints ?? [],
    solution: d.solution,
    misconceptionMap: d.misconceptionMap,
    involvesSkills: d.involvesSkills,
    provenance: d.provenance ?? 'GENERATED',
    speechLang: d.speechLang,
    timeEstimateSec: d.time ?? 30 + d.difficulty * 25,
  });
}

/* ------------------------------------------------------------------ */
/* Response/answer builders                                             */
/* ------------------------------------------------------------------ */

type Distractor = string | [label: string, misconceptionId: string];

export function choice(rng: Rng, correct: string, distractors: Distractor[], max = 4) {
  const seen = new Set([normalizeAnswer(correct)]);
  const cleaned: [string, string | undefined][] = [];
  for (const d of distractors) {
    const [label, mis] = typeof d === 'string' ? [d, undefined] : d;
    const key = normalizeAnswer(label);
    if (seen.has(key)) continue;
    seen.add(key);
    cleaned.push([label, mis]);
  }
  const chosen = cleaned.slice(0, max - 1);
  const options = rng.shuffle([
    { id: 'c', label: correct },
    ...chosen.map(([label], i) => ({ id: `d${i}`, label })),
  ]);
  const misconceptionMap: Record<string, string> = {};
  chosen.forEach(([, mis], i) => {
    if (mis) misconceptionMap[`d${i}`] = mis;
  });
  return {
    response: { type: 'choice', options } as ResponseSpec,
    answer: { type: 'choice', correct: 'c' } as AnswerKey,
    misconceptionMap,
  };
}

export function multi(rng: Rng, correct: string[], wrong: Distractor[]) {
  const opts = [
    ...correct.map((label, i) => ({ id: `c${i}`, label })),
    ...wrong.map((w, i) => ({ id: `w${i}`, label: typeof w === 'string' ? w : w[0] })),
  ];
  const misconceptionMap: Record<string, string> = {};
  wrong.forEach((w, i) => {
    if (typeof w !== 'string') misconceptionMap[`w${i}`] = w[1];
  });
  return {
    response: { type: 'multi', options: rng.shuffle(opts) } as ResponseSpec,
    answer: { type: 'multi', correct: correct.map((_, i) => `c${i}`) } as AnswerKey,
    misconceptionMap,
  };
}

export function numeric(value: number, tolerance = 1e-6, unit?: string, display?: string) {
  return {
    response: { type: 'numeric', unit } as ResponseSpec,
    answer: { type: 'numeric', value, tolerance, display: display ?? fmt(value) } as AnswerKey,
  };
}

export function text(accepted: string[], placeholder?: string, multiline = false) {
  return {
    response: { type: 'text', placeholder, multiline } as ResponseSpec,
    answer: { type: 'text', accepted, display: accepted[0] } as AnswerKey,
  };
}

export function matching(rng: Rng, pairs: [string, string][]) {
  const left = pairs.map(([l], i) => ({ id: `l${i}`, label: l }));
  const right = rng.shuffle(pairs.map(([, r], i) => ({ id: `r${i}`, label: r })));
  const map: Record<string, string> = {};
  pairs.forEach((_, i) => (map[`l${i}`] = `r${i}`));
  return {
    response: { type: 'match', left, right } as ResponseSpec,
    answer: { type: 'match', pairs: map } as AnswerKey,
  };
}

export function ordering(rng: Rng, itemsInOrder: string[]) {
  const items = itemsInOrder.map((label, i) => ({ id: `o${i}`, label }));
  let shuffled = rng.shuffle(items);
  // never present the items already in order
  if (shuffled.every((it, i) => it.id === items[i].id) && items.length > 1) shuffled = [...shuffled.slice(1), shuffled[0]];
  return {
    response: { type: 'order', items: shuffled } as ResponseSpec,
    answer: { type: 'order', sequence: items.map((i) => i.id) } as AnswerKey,
  };
}

export function categorize(rng: Rng, items: [label: string, category: string][], categories: string[]) {
  const cats = categories.map((c, i) => ({ id: `k${i}`, label: c }));
  const its = items.map(([label], i) => ({ id: `i${i}`, label }));
  const assignment: Record<string, string> = {};
  items.forEach(([, cat], i) => {
    const k = cats.find((c) => c.label === cat);
    if (!k) throw new Error(`categorize: unknown category ${cat}`);
    assignment[`i${i}`] = k.id;
  });
  return {
    response: { type: 'categorize', items: rng.shuffle(its), categories: cats } as ResponseSpec,
    answer: { type: 'categorize', assignment } as AnswerKey,
  };
}

export function steps(list: { label: string; value?: number; accepted?: string[]; unit?: string; tolerance?: number }[]) {
  return {
    response: {
      type: 'steps',
      steps: list.map((s, i) => ({ id: `s${i}`, label: s.label, input: s.value !== undefined ? 'numeric' : 'text', unit: s.unit })),
    } as ResponseSpec,
    answer: {
      type: 'steps',
      values: Object.fromEntries(
        list.map((s, i) => [`s${i}`, s.value !== undefined ? { value: s.value, tolerance: s.tolerance ?? 1e-6 } : { accepted: s.accepted ?? [] }]),
      ),
    } as AnswerKey,
  };
}

/** Sentence construction from word tiles. `sentences` lists every acceptable full sentence. */
export function tokens(rng: Rng, sentences: string[], extra: string[] = []) {
  const words = sentences[0].split(' ');
  const tokensList = rng.shuffle([...words, ...extra].map((w, i) => ({ id: `t${i}`, label: w })));
  return {
    response: { type: 'tokens', tokens: tokensList } as ResponseSpec,
    answer: { type: 'tokens', sentences } as AnswerKey,
  };
}

export function tfReason(rng: Rng, truth: boolean, correctReason: string, wrongReasons: Distractor[]) {
  const reasons = rng.shuffle([
    { id: 'r', label: correctReason },
    ...wrongReasons.map((w, i) => ({ id: `x${i}`, label: typeof w === 'string' ? w : w[0] })),
  ]);
  const misconceptionMap: Record<string, string> = {};
  wrongReasons.forEach((w, i) => {
    if (typeof w !== 'string') misconceptionMap[`x${i}`] = w[1];
  });
  return {
    response: { type: 'tf-reason', reasons } as ResponseSpec,
    answer: { type: 'tf-reason', truth, reason: 'r' } as AnswerKey,
    misconceptionMap,
  };
}

export function pair(rng: Rng, firstLabel: string, secondLabel: string, first: [string, string[]], second: [string, string[]]) {
  const f = rng.shuffle([first[0], ...first[1]]).map((label, i) => ({ id: `f${i}`, label }));
  const s = rng.shuffle([second[0], ...second[1]]).map((label, i) => ({ id: `s${i}`, label }));
  return {
    response: { type: 'pair', first: f, second: s, firstLabel, secondLabel } as ResponseSpec,
    answer: {
      type: 'pair',
      first: f.find((o) => o.label === first[0])!.id,
      second: s.find((o) => o.label === second[0])!.id,
    } as AnswerKey,
  };
}

export function rubric(criteria: RubricCriterion[], minScore: number, exemplar: string, placeholder?: string) {
  return {
    response: { type: 'text', multiline: true, placeholder } as ResponseSpec,
    answer: { type: 'rubric', criteria, minScore, exemplar } as AnswerKey,
  };
}

export function validator(validatorId: string, params: Record<string, number | string>, display: string, placeholder?: string) {
  return {
    response: { type: 'text', placeholder } as ResponseSpec,
    answer: { type: 'validator', validatorId, params, display } as AnswerKey,
  };
}

/* ------------------------------------------------------------------ */
/* Number formatting                                                    */
/* ------------------------------------------------------------------ */

export function fmt(n: number, maxDecimals = 6): string {
  if (!Number.isFinite(n)) return String(n);
  const r = parseFloat(n.toFixed(maxDecimals));
  return Object.is(r, -0) ? '0' : String(r);
}

/** Wrap negatives in parentheses for substitution displays. */
export const paren = (n: number) => (n < 0 ? `(${fmt(n)})` : fmt(n));

/** Format a signed term: +3x / −3x, used inside expressions. */
export function term(coef: number, v = '', first = false): string {
  if (coef === 0) return '';
  const abs = Math.abs(coef);
  const body = v ? (abs === 1 ? v : `${fmt(abs)}${v}`) : fmt(abs);
  if (first) return coef < 0 ? `−${body}` : body;
  return coef < 0 ? ` − ${body}` : ` + ${body}`;
}

/** Build an expression from [coef, variable] pairs, skipping zeros. */
export function expr(terms: [number, string][]): string {
  const nz = terms.filter(([c]) => c !== 0);
  if (!nz.length) return '0';
  return nz.map(([c, v], i) => term(c, v, i === 0)).join('');
}

export const minus = (s: string) => s.replace(/-/g, '−');

export function frac(n: number, d: number): string {
  if (d < 0) {
    n = -n;
    d = -d;
  }
  return d === 1 ? fmt(n) : `[[${n}/${d}]]`;
}

export function fracPlain(n: number, d: number): string {
  if (d < 0) {
    n = -n;
    d = -d;
  }
  return d === 1 ? fmt(n) : `${n}/${d}`;
}

/** Pick a "form" (structural variant) appropriate to difficulty, avoiding recently used ones. */
export function pickForm<T extends { id: string; min: number; max: number }>(ctx: GenContext, skillId: string, forms: T[]): T {
  const eligible = forms.filter((f) => ctx.difficulty >= f.min && ctx.difficulty <= f.max);
  const pool = eligible.length ? eligible : forms;
  const fresh = pool.filter((f) => !ctx.avoid.has(`form:${skillId}:${f.id}`));
  const pref = ctx.preferKinds;
  const source = fresh.length ? fresh : pool;
  if (pref && pref.length) {
    const kindMatch = source.filter((f) => (f as unknown as { kinds?: QuestionKind[] }).kinds?.some((k) => pref.includes(k)));
    if (kindMatch.length) return ctx.rng.pick(kindMatch);
  }
  return ctx.rng.pick(source);
}

export const CONTEXT_NAMES = ['Ingrid', 'Amir', 'Sofie', 'Jonas', 'Leah', 'Mateusz', 'Aisha', 'Erik', 'Nora', 'Liam', 'Maja', 'Tobias', 'Selma', 'Ali', 'Ella', 'Kasper'];
